import { api } from './client'
import type { ExpenseCategory } from './expense-category-api'

export type ExpensePaymentMethod = 'cash' | 'bank_transfer'
export type ExpensePaymentStatus = 'paid' | 'unpaid'
// CategorySource: ai đặt category_id hiện tại. 'manual' là chốt chặn — quy
// tắc tự phân loại (HD-160) không bao giờ ghi đè một dòng người dùng đã tự
// tay chọn/sửa.
export type ExpenseCategorySource = 'manual' | 'rule' | 'fallback'

export interface Expense {
    id: string
    category_id: string
    vendor_name: string
    amount: number
    expense_date: string
    payment_method: ExpensePaymentMethod
    payment_status: ExpensePaymentStatus
    paid_at?: string
    note?: string
    created_by: string
    creator_name?: string
    category?: ExpenseCategory
    // balance_event_id != null (suy ra từ bank_counterparty_* có mặt) = khoản
    // chi sinh từ sao kê ngân hàng — amount/expense_date/payment_method bị
    // khoá trên form, chỉ sửa được category/vendor/note.
    balance_event_id?: string
    category_rule_id?: string
    category_source: ExpenseCategorySource
    possible_duplicate: boolean
    // spent_at: mốc thời gian THỰC của khoản chi (giờ:phút). undefined =
    // không biết giờ thật — không phải "00:00". Khi có mặt, expense_date
    // luôn là ngày lịch Việt Nam của spent_at (bất biến phía server).
    spent_at?: string
    bank_counterparty_name?: string
    bank_counterparty_account?: string
    // Nội dung chuyển khoản của giao dịch ngân hàng (balance_events.content)
    // — undefined với khoản chi nhập tay.
    bank_transfer_content?: string
    created_at: string
    updated_at: string
    deleted_at?: string
}

export interface UpsertExpenseRequest {
    category_id: string
    vendor_name: string
    amount: number
    expense_date: string
    payment_method: ExpensePaymentMethod
    payment_status: ExpensePaymentStatus
    note?: string
    // spent_at: RFC3339, tuỳ chọn. Có mặt → server tự suy expense_date từ đó
    // (ngày lịch Việt Nam), không tin expense_date gửi kèm. undefined/omit
    // → xoá spent_at (chỉ còn biết ngày).
    spent_at?: string
}

export interface ListExpensesParams {
    search?: string
    category_id?: string
    payment_method?: ExpensePaymentMethod
    payment_status?: ExpensePaymentStatus
    from_date?: string
    to_date?: string
    possible_duplicate?: boolean
    bank_sourced?: boolean
    category_source?: ExpenseCategorySource
    page?: number
    per_page?: number
}

export const expenseApi = {
    list: (params?: ListExpensesParams) =>
        api.get<Expense[]>('/expenses', {
            params: params as Record<
                string,
                string | number | boolean | undefined
            >,
        }),
    getById: (id: string) => api.get<Expense>(`/expenses/${id}`),
    create: (data: UpsertExpenseRequest) =>
        api.post<Expense>('/expenses', data),
    update: (id: string, data: UpsertExpenseRequest) =>
        api.put<Expense>(`/expenses/${id}`, data),
    delete: (id: string) => api.delete(`/expenses/${id}`),
    clearPossibleDuplicate: (id: string) =>
        api.post<Expense>(`/expenses/${id}/clear-duplicate`, {}),
    // "Gộp vào khoản nhập tay" candidates: manual expenses sharing this
    // bank-sourced expense's date+amount. Never the pre-HD-160
    // /expense-sync/{event}/candidates endpoint — that one 409s once the
    // balance event is matched, which import always does before this
    // dialog can open.
    mergeCandidates: (autoExpenseId: string) =>
        api.get<Expense[]>(`/expenses/${autoExpenseId}/merge-candidates`),
    mergeIntoManual: (autoExpenseId: string, manualExpenseId: string) =>
        api.post<Expense>(`/expenses/${autoExpenseId}/merge-into-manual`, {
            manual_expense_id: manualExpenseId,
        }),
    bulkSetCategory: (ids: string[], categoryId: string) =>
        api.post('/expenses/bulk-category', {
            ids,
            category_id: categoryId,
        }),
}
